import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const PhotoUpload = ({ formData, updateFormData, errors }) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const photos = formData?.photos || [];

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(true);
    } else if (e?.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    const files = Array.from(e?.dataTransfer?.files || []);
    handleFiles(files);
  };

  const handleFileInput = (e) => {
    const files = Array.from(e?.target?.files || []);
    handleFiles(files);
  };

  const uploadToStorage = async (file) => {
    const ext = file?.name?.split('.')?.pop();
    const fileName = `${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}.${ext}`;
    const filePath = `project-photos/${fileName}`;

    const { error } = await supabase?.storage?.from('project-photos')?.upload(filePath, file, { cacheControl: '3600', upsert: false });

    if (error) throw error;

    const { data: urlData } = supabase?.storage?.from('project-photos')?.getPublicUrl(filePath);

    return {
      id: `photo_${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`,
      name: file?.name,
      size: file?.size,
      type: file?.type,
      url: urlData?.publicUrl,
      filePath,
      uploadedAt: new Date()?.toISOString()
    };
  };

  const handleFiles = async (files) => {
    const imageFiles = files?.filter(file => file?.type?.startsWith('image/'));
    if (imageFiles?.length === 0) return;

    setUploading(true);
    try {
      const uploadedPhotos = await Promise.all(imageFiles?.map(uploadToStorage));
      updateFormData('photos', [...photos, ...uploadedPhotos]);
    } catch (error) {
      console.error('Error uploading files:', error);
      // Fallback to base64 if storage upload fails
      const filePromises = imageFiles?.map(file => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            resolve({
              id: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              name: file?.name,
              size: file?.size,
              type: file?.type,
              dataUrl: e.target?.result,
              uploadedAt: new Date().toISOString()
            });
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      });
      const fallbackPhotos = await Promise.all(filePromises);
      updateFormData('photos', [...photos, ...fallbackPhotos]);
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = async (photo) => {
    // Remove from Supabase Storage if it has a filePath
    if (photo?.filePath) {
      try {
        await supabase?.storage?.from('project-photos')?.remove([photo?.filePath]);
      } catch (err) {
        console.warn('Could not remove file from storage:', err);
      }
    }
    const updatedPhotos = photos?.filter(p => p?.id !== photo?.id);
    updateFormData('photos', updatedPhotos);
  };

  const handleBrowseClick = () => {
    fileInputRef?.current?.click();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes?.[i];
  };

  const getPhotoSrc = (photo) => {
    if (typeof photo === 'string') return photo;
    return photo?.url || photo?.dataUrl || null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-foreground mb-2">Upload Photos</h2>
        <p className="text-text-secondary">
          Share photos with vendors to help them better understand your project needs. This is optional but recommended.
        </p>
      </div>
      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragActive
            ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            {uploading ? (
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            ) : (
              <Icon name="Upload" size={32} className="text-primary" />
            )}
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">
            {uploading ? 'Uploading photos...' : 'Drag and drop photos here'}
          </h3>
          {!uploading && (
            <>
              <p className="text-text-secondary mb-4">or</p>
              <Button
                type="button"
                variant="outline"
                onClick={handleBrowseClick}
              >
                <Icon name="FolderOpen" size={16} className="mr-2" />
                Browse Files
              </Button>
              <p className="text-xs text-text-secondary mt-4">
                Supported formats: JPG, PNG, GIF, WebP (Max 10MB per file)
              </p>
            </>
          )}
        </div>
      </div>
      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*"
        onChange={handleFileInput}
        className="hidden"
      />
      {/* Uploaded Photos Grid */}
      {photos?.length > 0 && (
        <div>
          <h3 className="text-lg font-medium text-foreground mb-4">
            Uploaded Photos ({photos?.length})
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {photos?.map((photo) => {
              const src = getPhotoSrc(photo);
              return (
                <div
                  key={photo?.id}
                  className="relative group bg-card border border-border rounded-lg overflow-hidden"
                >
                  {/* Photo Preview */}
                  <div className="aspect-square bg-background">
                    {src ? (
                      <img
                        src={src}
                        alt={photo?.name || 'Project photo'}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="w-full h-full items-center justify-center bg-surface" style={{ display: src ? 'none' : 'flex' }}>
                      <Icon name="ImageOff" size={24} className="text-text-secondary" />
                    </div>
                  </div>

                  {/* Photo Info */}
                  <div className="p-2">
                    <p className="text-xs text-foreground truncate" title={photo?.name}>
                      {photo?.name}
                    </p>
                    <p className="text-xs text-text-secondary">
                      {formatFileSize(photo?.size)}
                    </p>
                  </div>

                  {/* Remove Button */}
                  <button
                    type="button"
                    onClick={() => handleRemovePhoto(photo)}
                    className="absolute top-2 right-2 w-8 h-8 bg-error text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-error/90"
                    title="Remove photo"
                  >
                    <Icon name="X" size={16} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Icon name="Info" size={16} className="text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-900">
            <p className="font-medium mb-1">Why upload photos?</p>
            <ul className="list-disc list-inside space-y-1 text-blue-800">
              <li>Help vendors understand your project requirements better</li>
              <li>Show existing conditions or desired outcomes</li>
              <li>Reduce back-and-forth communication</li>
              <li>Get more accurate quotes</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoUpload;